import React, { useState } from 'react';
import {
    PageTitle,
    PageDescription,
} from 'components/common/Study/PageComponents';
import lodash from 'lodash';
import classnames from 'classnames';
import { useImmer } from 'use-immer';
import { Button, BUTTON } from 'components/common/Button/Button';
import DismissiblePanel from 'components/common/DismissiblePanel';
import { DismissibleHeader } from 'components/common/DismissiblePanel';
import { TeamMembers } from 'components/common/Study/ListItemCards/StudyTeamMemberCard';
import styles from './StudyTeamV2.scss';
import StudyTeamMemberForm, { STUDY_USER_TYPE } from 'components/common/Study/StudyTeamMemberForm';
import { PrimaryStepAction } from 'pages/study/containers/components/StudyFormField';


export default function StudyTeamV2(props) {
    const [isPanelShowing, showPanel] = useState(false);
    const [isEditInProgress, editInProgress] = useState(false);
    const { stepData: { isDraft = false }, onExit } = props;

    const studyTeam =
        lodash.orderBy(props.stepData.fields.studyTeam || [], ['studyRole.name', 'displayName']) || [];

    const defaultTeamMember = {
        userId: '',
        sub: '',
        displayName: '',
        studyRoleId: '',
        studyRole: {
            id: '',
            name: '',
        },
        nameOfResearcher: '',
    };

    const [dismissibleDraft, setDismissibleDraft] = useImmer(defaultTeamMember);
    const [dismissibleEdit, setDismissibleEdit] = useImmer(defaultTeamMember);

    const onEdit = e => {
        editInProgress(true);

        setDismissibleDraft(draft => {
            draft.id = e.id;
            draft.userId = e.userId;
            draft.sub = e.sub;
            draft.displayName = e.displayName;
            draft.studyRoleId = e.studyRoleId;
            draft.studyRole.name = (e.studyRole) ? e.studyRole.name : '';
            draft.studyRole.id = (e.studyRole) ? e.studyRole.id : '';
            draft.nameOfResearcher = e.nameOfResearcher;
        });

        setDismissibleEdit(draft => {
            draft.id = e.id;
            draft.userId = e.userId;
            draft.sub = e.sub;
            draft.displayName = e.displayName;
            draft.studyRoleId = e.studyRoleId;
            draft.studyRole.name = (e.studyRole) ? e.studyRole.name : '';
            draft.studyRole.id = (e.studyRole) ? e.studyRole.id : '';
            draft.nameOfResearcher = e.nameOfResearcher;
        });
    };

    const onClear = studyTeamMember => {
        const studyTeam = lodash.get(props, 'stepData.fields.studyTeam', []);
        const newStudyTeam = studyTeam.filter(s => s.userId !== studyTeamMember.userId);
        props.setField('studyTeam', lodash.clone(newStudyTeam));
    };

    const onNext = () => {
        props.progressToNextStep({});
    };

    const onSave = (studyTeamMember) => {
        if (lodash.isEqual(studyTeamMember, defaultTeamMember)) return;

        const studyTeam = lodash.get(props, 'stepData.fields.studyTeam', []);
        const draftStudyTeam = studyTeam.filter(s => s.userId !== studyTeamMember.userId);
        const newStudyTeam = [...draftStudyTeam, studyTeamMember];

        props.setField('studyTeam', lodash.clone(newStudyTeam));
    };

    return (
        <div className={
            classnames(
                styles.studyTeam,
                { [styles.panelOpen]: isPanelShowing, }
            )
        }>
            <PageTitle>Study Team</PageTitle>
            <PageDescription>Add team members to this study.</PageDescription>
            <AddTeamMemberButton
                className={styles.addTeamMemberButton}
                onClick={() => showPanel(true)}
            />
            <StudyTeamContent
                hideNextButton={isPanelShowing}
                studyTeam={studyTeam}
                onClear={value => onClear(value)}
                onNext={onNext}
                onEditDetails={value => {
                    editInProgress(true);
                    onEdit(value);
                    showPanel(true);
                }}
                isDraft={isDraft}
                onExit={onExit}
            />
            <div className={styles.addTeamMemberPanel}>
                <AddTeamMemberPanel
                    dismiss={isPanelShowing}
                    onAdd={value => {
                        onSave(value);
                        editInProgress(false);
                    }}
                    onEdit={isEditInProgress}
                    onDismiss={() => showPanel(false)}
                    onDismissDraft={() => {
                        onSave(dismissibleDraft);
                        editInProgress(false);
                    }}
                    defaults={dismissibleEdit}
                    manageDefaults={setDismissibleEdit}
                    {...props}
                />
            </div>
        </div>
    );
}

export const StudyTeamContent = ({ onClear, onEditDetails, onNext, hideNextButton, studyTeam, isDraft, onExit }) => {
    return (
        <div className={classnames(
            styles.studyTeamContent, { [styles.panelVisible]: hideNextButton, }
        )}
        >
            <TeamMembers
                onClear={onClear}
                onEdit={onEditDetails}
                studyTeam={studyTeam}
                fromCreateStudyWorkflow={true}
            />
            {!hideNextButton &&
                <PrimaryStepAction 
                    saveButtonText='Save and Continue'
                    exitButtonText={`Exit ${isDraft ? 'Draft' : 'Study'}`}
                    onClickSave={onNext}
                    onClickExit={onExit}
                />
            }
        </div >
    );
};

export const AddTeamMemberPanel = (props) => {
    const { defaults, manageDefaults, onEdit } = props;

    const [potentialMatches, setPotentialMatches] = useState([]);
    const teamMembers = getTeamMemberSelection(props);
    const studyRoles = lodash.get(props, 'stepData.studyRoles', []);
    const staffDescriptor = lodash.get(props, 'stepData.staffDescriptor.dataSources', []);

    const teamMembersOptions = teamMembers.map(teamMember => (
        {
            value: teamMember.userId,
            label: teamMember.displayName,
            sub: teamMember.sub,
        }
    ));

    const studyRolesOptions = studyRoles.map(role => (
        { value: role.id, label: role.name })
    );

    const staffDescriptorOptions = staffDescriptor.map(staffDescriptor => (
        { value: staffDescriptor.value, label: staffDescriptor.text })
    );

    const isFormReady = () =>
        defaults.userId !== '' && defaults.studyRoleId !== '' && defaults.nameOfResearcher !== '';

    function dismissForm() {
        manageDefaults(draft => {
            draft.id = '';
            draft.userId = '';
            draft.sub = '';
            draft.displayName = '';
            draft.studyRoleId = '';
            draft.studyRole.id = '';
            draft.studyRole.name = '';
            draft.nameOfResearcher = '';
        });

        setPotentialMatches([]);
        props.onDismiss();
    }

    function getTeamMemberSelection(props) {
        const existingTeamMembers = lodash.get(props, 'stepData.fields.studyTeam', [])
            .filter(t => t.userId !== defaults.userId);

        const allTeamMembers = lodash.get(props, 'stepData.teamMembers', []);
        const uniqueTeamMembers = lodash.uniqBy(allTeamMembers, 'userId');
        return lodash.differenceBy(uniqueTeamMembers, existingTeamMembers, 'userId');
    }

    return (
        <DismissiblePanel            
            dismiss={!props.dismiss}
            onDismiss={() => {
                props.onDismissDraft();
                dismissForm();
            }}
        >
            <DismissibleHeader
                title={!onEdit ? 'Add Team Member' : 'Edit Team Member'}
                message={!onEdit &&
                    'A team member is an internal user who is assigned to a role to carry out delegated study tasks.'}
            />
            <div className={styles.addTeamMemberPanelContents}>
                <div>
                    <StudyTeamMemberForm
                        editStatus={onEdit}
                        form={defaults}
                        setForm={manageDefaults}
                        potentialMatches={potentialMatches}
                        setPotentialMatches={setPotentialMatches}
                        teamMembersOptions={teamMembersOptions}
                        studyRolesOptions={studyRolesOptions}
                        staffDescriptorOptions={staffDescriptorOptions}
                        studyMemberType={STUDY_USER_TYPE.TEAM}
                    />
                </div>
                <ActionRow
                    onCancel={() => {
                        props.onDismissDraft();
                        dismissForm();
                    }}
                    onEdit={onEdit}
                    onPrimaryAction={() => {
                        props.onAdd({
                            id: defaults.id,
                            userId: defaults.userId,
                            sub: defaults.sub,
                            displayName: defaults.displayName,
                            studyRoleId: defaults.studyRoleId,
                            studyRole: {
                                name: defaults.studyRole.name,
                                id: defaults.studyRoleId,
                            },
                            nameOfResearcher: defaults.nameOfResearcher,
                        });

                        dismissForm();
                    }}

                    primaryActionDisabled={!isFormReady()}
                />
            </div>
        </DismissiblePanel>
    );
};

export const ActionRow = (props) => (
    <div className={styles.actionRow}>
        <Button
            bgcolor={BUTTON.BGCOLOR.CLEAR}
            color={BUTTON.COLOR.BLACK}
            type={BUTTON.SIZE.SECONDARY}
            onClick={props.onCancel}
        >
            Cancel
        </Button>
        <Button
            bgcolor={BUTTON.BGCOLOR.ORANGE}
            color={BUTTON.COLOR.WHITE}
            onClick={props.onPrimaryAction}
            disabled={props.primaryActionDisabled}
        >
            {!props.onEdit ? 'Add Team Member' : 'Confirm'}
        </Button>
    </div>
);

export const NextButton = (props) => (
    <div className={styles.nextButton}>
        <Button
            className={props.className}
            bgcolor={BUTTON.BGCOLOR.ORANGE}
            color={BUTTON.COLOR.WHITE}
            onClick={props.onClick}
        >
            Save and Continue
        </Button>
    </div>
);

export const AddTeamMemberButton = (props) => (
    <div>
        <Button
            className={props.className}
            bgcolor={BUTTON.BGCOLOR.BLUE}
            color={BUTTON.COLOR.WHITE}
            onClick={props.onClick}
        >
            Add Team Member
        </Button>
    </div>
);
